/**
 * Perfex CRM API Service
 * Wrapper for GreenTech Shredding Perfex CRM v3.x REST API
 *
 * Documentation: https://perfexcrm.themesic.com/apiguide/
 */

// Get configuration from global CONFIG object
const getConfig = () => {
    if (typeof window !== 'undefined' && window.CONFIG) {
        return window.CONFIG.PERFEX_API;
    }
    // Fallback for testing/development
    return {
        BASE_URL: 'https://manage.greentechshredding.com/api/',
        AUTH_TOKEN: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ1c2VyIjoiQVBJIENvbm5lY3Rpb24gUHJvZCIsIm5hbWUiOiJjb25uZWN0b3IiLCJBUElfVElNRSI6MTc0NTIwNDc2Mn0.KxCYYgOGNJjQw5IPix8SurmILpW9qsZ0_H9RLTeA9rA',
        ENDPOINTS: {
            LEADS: 'leads',
            TASKS: 'tasks'
        },
        ITEMS_PER_PAGE: 20,
        TIMEOUT: 30000
    };
};

/**
 * Make an API request to Perfex CRM
 * @param {string} endpoint - API endpoint (e.g., 'leads', 'tasks/123')
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Response data
 */
async function apiRequest(endpoint, method = 'GET', data = null, options = {}) {
    const config = getConfig();

    // Handle proxy mode
    const useProxy = config.USE_PROXY === true;
    const url = useProxy
        ? `${config.BASE_URL}${endpoint}`  // Proxy URL already includes query param
        : `${config.BASE_URL}${endpoint}`;

    const headers = {
        'Accept': 'application/json',
        ...options.headers
    };

    // Only add authtoken if not using proxy (proxy handles authentication)
    if (!useProxy && config.AUTH_TOKEN) {
        headers['authtoken'] = config.AUTH_TOKEN;
    }

    const fetchOptions = {
        method,
        headers,
        ...options
    };

    // Add timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.TIMEOUT);
    fetchOptions.signal = controller.signal;

    try {
        // Handle request body based on method and data
        if (data && (method === 'POST' || method === 'PUT')) {
            // Perfex API accepts both JSON and form-urlencoded
            // Using JSON for simplicity
            headers['Content-Type'] = 'application/json';
            fetchOptions.body = JSON.stringify(data);
        }

        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);

        // Handle HTTP errors
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error ${response.status}: ${errorText}`);
        }

        // Parse JSON response
        const result = await response.json();
        return { success: true, data: result };

    } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
            throw new Error('Request timeout - please check your connection');
        }

        console.error('API Request Error:', error);
        throw error;
    }
}

// ==================== LEADS API ====================

/**
 * Get all leads with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Items per page
 * @returns {Promise} - Array of leads
 */
export async function getLeads(page = 1, limit = null) {
    try {
        const config = getConfig();
        const itemsPerPage = limit || config.ITEMS_PER_PAGE;

        // Perfex API pagination uses offset
        const offset = (page - 1) * itemsPerPage;

        // Note: Perfex API endpoint structure may vary
        // Adjust based on actual API documentation
        const endpoint = `${config.ENDPOINTS.LEADS}?offset=${offset}&limit=${itemsPerPage}`;

        const result = await apiRequest(endpoint, 'GET');
        return result;
    } catch (error) {
        console.error('Error fetching leads:', error);
        throw error;
    }
}

/**
 * Get a specific lead by ID
 * @param {number} id - Lead ID
 * @returns {Promise} - Lead object
 */
export async function getLead(id) {
    try {
        const config = getConfig();
        const endpoint = `${config.ENDPOINTS.LEADS}/${id}`;

        const result = await apiRequest(endpoint, 'GET');
        return result;
    } catch (error) {
        console.error(`Error fetching lead ${id}:`, error);
        throw error;
    }
}

/**
 * Create a new lead
 * @param {Object} leadData - Lead data object
 * @returns {Promise} - Created lead object
 */
export async function createLead(leadData) {
    try {
        const config = getConfig();
        const endpoint = config.ENDPOINTS.LEADS;

        // Format custom fields for Perfex API
        const formattedData = formatLeadData(leadData);

        const result = await apiRequest(endpoint, 'POST', formattedData);
        return result;
    } catch (error) {
        console.error('Error creating lead:', error);
        throw error;
    }
}

/**
 * Update an existing lead
 * @param {number} id - Lead ID
 * @param {Object} leadData - Updated lead data
 * @returns {Promise} - Updated lead object
 */
export async function updateLead(id, leadData) {
    try {
        const config = getConfig();
        const endpoint = `${config.ENDPOINTS.LEADS}/${id}`;

        // Format custom fields for Perfex API
        const formattedData = formatLeadData(leadData);

        const result = await apiRequest(endpoint, 'PUT', formattedData);
        return result;
    } catch (error) {
        console.error(`Error updating lead ${id}:`, error);
        throw error;
    }
}

/**
 * Delete a lead
 * @param {number} id - Lead ID
 * @returns {Promise} - Success response
 */
export async function deleteLead(id) {
    try {
        const config = getConfig();
        const endpoint = `${config.ENDPOINTS.LEADS}/${id}`;

        const result = await apiRequest(endpoint, 'DELETE');
        return result;
    } catch (error) {
        console.error(`Error deleting lead ${id}:`, error);
        throw error;
    }
}

/**
 * Format lead data for Perfex API
 * Handles custom fields conversion
 * @param {Object} leadData - Raw lead data
 * @returns {Object} - Formatted lead data
 */
function formatLeadData(leadData) {
    const formatted = { ...leadData };

    // If customfields exist, format them according to Perfex API spec
    if (leadData.customfields && Array.isArray(leadData.customfields)) {
        // Perfex expects: customfields[lead] = JSON string of array
        formatted['customfields[lead]'] = JSON.stringify(leadData.customfields);
        delete formatted.customfields;
    }

    return formatted;
}

// ==================== TASKS API ====================

/**
 * Get all tasks with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Items per page
 * @returns {Promise} - Array of tasks
 */
export async function getTasks(page = 1, limit = null) {
    try {
        const config = getConfig();
        const itemsPerPage = limit || config.ITEMS_PER_PAGE;

        const offset = (page - 1) * itemsPerPage;
        const endpoint = `${config.ENDPOINTS.TASKS}?offset=${offset}&limit=${itemsPerPage}`;

        const result = await apiRequest(endpoint, 'GET');
        return result;
    } catch (error) {
        console.error('Error fetching tasks:', error);
        throw error;
    }
}

/**
 * Get a specific task by ID
 * @param {number} id - Task ID
 * @returns {Promise} - Task object
 */
export async function getTask(id) {
    try {
        const config = getConfig();
        const endpoint = `${config.ENDPOINTS.TASKS}/${id}`;

        const result = await apiRequest(endpoint, 'GET');
        return result;
    } catch (error) {
        console.error(`Error fetching task ${id}:`, error);
        throw error;
    }
}

/**
 * Create a new task
 * @param {Object} taskData - Task data object
 * @returns {Promise} - Created task object
 */
export async function createTask(taskData) {
    try {
        const config = getConfig();
        const endpoint = config.ENDPOINTS.TASKS;

        const result = await apiRequest(endpoint, 'POST', taskData);
        return result;
    } catch (error) {
        console.error('Error creating task:', error);
        throw error;
    }
}

/**
 * Update an existing task
 * @param {number} id - Task ID
 * @param {Object} taskData - Updated task data
 * @returns {Promise} - Updated task object
 */
export async function updateTask(id, taskData) {
    try {
        const config = getConfig();
        const endpoint = `${config.ENDPOINTS.TASKS}/${id}`;

        const result = await apiRequest(endpoint, 'PUT', taskData);
        return result;
    } catch (error) {
        console.error(`Error updating task ${id}:`, error);
        throw error;
    }
}

/**
 * Delete a task
 * @param {number} id - Task ID
 * @returns {Promise} - Success response
 */
export async function deleteTask(id) {
    try {
        const config = getConfig();
        const endpoint = `${config.ENDPOINTS.TASKS}/${id}`;

        const result = await apiRequest(endpoint, 'DELETE');
        return result;
    } catch (error) {
        console.error(`Error deleting task ${id}:`, error);
        throw error;
    }
}

/**
 * Mark a task as complete
 * @param {number} id - Task ID
 * @returns {Promise} - Updated task object
 */
export async function completeTask(id) {
    try {
        // Update task status to completed
        // Note: Perfex task status IDs may vary - adjust as needed
        const result = await updateTask(id, { status: 5 }); // 5 is typically "Complete" in Perfex
        return result;
    } catch (error) {
        console.error(`Error completing task ${id}:`, error);
        throw error;
    }
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Test API connection
 * @returns {Promise} - Connection status
 */
export async function testConnection() {
    try {
        // Try to fetch a single lead to test connection
        const result = await apiRequest('leads?limit=1', 'GET');
        return { success: true, message: 'Connection successful', data: result };
    } catch (error) {
        return { success: false, message: error.message, error };
    }
}

/**
 * Parse custom fields from Perfex lead response
 * @param {Object} lead - Lead object from API
 * @returns {Object} - Lead with parsed custom fields
 */
export function parseLeadCustomFields(lead) {
    if (!lead) return lead;

    // Custom fields are already parsed in the response
    // This function can be used for additional processing if needed
    const parsed = { ...lead };

    // Convert customfields array to object for easier access
    if (parsed.customfields && Array.isArray(parsed.customfields)) {
        parsed.customFieldsMap = {};
        parsed.customfields.forEach(field => {
            parsed.customFieldsMap[field.label] = field.value;
        });
    }

    return parsed;
}

/**
 * Get custom field value from lead
 * @param {Object} lead - Lead object
 * @param {string} fieldName - Custom field label
 * @returns {string} - Field value or empty string
 */
export function getCustomField(lead, fieldName) {
    if (!lead || !lead.customfields) return '';

    const field = lead.customfields.find(f => f.label === fieldName);
    return field ? field.value : '';
}

/**
 * Update custom field value in lead object
 * @param {Object} lead - Lead object
 * @param {string} fieldName - Custom field label
 * @param {string} value - New value
 * @returns {Object} - Updated lead object
 */
export function setCustomField(lead, fieldName, value) {
    const updated = { ...lead };

    if (!updated.customfields) {
        updated.customfields = [];
    }

    const fieldIndex = updated.customfields.findIndex(f => f.label === fieldName);

    if (fieldIndex >= 0) {
        updated.customfields[fieldIndex].value = value;
    } else {
        updated.customfields.push({ label: fieldName, value });
    }

    return updated;
}
